import { getUser } from "@/components/session/JwtTokenManager";
import { ShareGroup, ShareItemData, ShareUser } from "@/components/ui/sharing";
import { PermissionLevel } from "@/graphql";
import {
  ProcessedPermissions,
  ResourcePermission,
  ResourcePermissionGroup,
  ResourcePermissionProgram,
  ResourcePermissionUser,
  SharePermissionState,
} from "@/types/permissions";

/**
 * Process raw permissions data and categorize by type
 */
export function processResourcePermissions(
  permissions: ResourcePermission[],
): ProcessedPermissions {
  const result: ProcessedPermissions = {
    users: [],
    groups: [],
    programs: [],
  };

  permissions.forEach((permission) => {
    if (permission.user) {
      const userEntry: ResourcePermissionUser = {
        ...permission.user,
        shareStatus: permission.permissionLevel as SharePermissionState,
        originalPermissionLevel: permission.permissionLevel,
        permissionId: permission.id,
        isOwner: permission.accessScope === "Owner",
      };

      if (permission.accessScope === "Owner") {
        result.users.unshift(userEntry); // Owner goes first
        result.owner = userEntry;
      } else {
        result.users.push(userEntry);
      }
    } else if (permission.workspaceGroup) {
      result.groups.push({
        ...permission.workspaceGroup,
        shareStatus: permission.permissionLevel as SharePermissionState,
        originalPermissionLevel: permission.permissionLevel,
        permissionId: permission.id,
      });
    } else if (permission.program) {
      result.programs.push({
        ...permission.program,
        shareStatus: permission.permissionLevel as SharePermissionState,
        originalPermissionLevel: permission.permissionLevel,
        permissionId: permission.id,
      });
    }
  });

  return result;
}

/**
 * Convert permission data to ShareItemData format for UI components
 */
export function convertToShareItems(
  users: ResourcePermissionUser[],
  groups: ResourcePermissionGroup[],
  programs: ResourcePermissionProgram[],
  availablePermissions: SharePermissionState[] = ["VIEW", "EDIT", "SHARE"],
): ShareItemData[] {
  const currentUserId = getUser()?.id;

  // Helper to determine if current user can modify a permission
  const canModifyPermission = (
    currentPermission: SharePermissionState,
  ): boolean => {
    // If user has no available permissions, they can't modify anything
    if (availablePermissions.length === 0) return false;

    // Check if the current permission is within what the user can grant
    const permissionOrder = ["VIEW", "SHARE", "EDIT"];
    const maxGrantableIndex = Math.max(
      ...availablePermissions.map((p) => permissionOrder.indexOf(p)),
    );
    const currentPermissionIndex = permissionOrder.indexOf(currentPermission);

    return currentPermissionIndex <= maxGrantableIndex;
  };

  const userItems: ShareItemData[] = users.map((user) => {
    const userPermission = user.shareStatus || "VIEW";
    const canModify = !user.isOwner && canModifyPermission(userPermission);

    return {
      type: "user" as const,
      item: {
        id: user.id,
        email: user.email || "",
        name: user.name || undefined,
      } as ShareUser,
      shareStatus: userPermission,
      isOwner: user.isOwner,
      isMe: user.id === currentUserId,
      canModifyPermissions: canModify,
      availablePermissions: user.isOwner ? [] : availablePermissions,
    };
  });

  const groupItems: ShareItemData[] = groups.map((group) => {
    const groupPermission = group.shareStatus || "VIEW";
    const canModify = canModifyPermission(groupPermission);

    return {
      type: "group" as const,
      item: {
        id: group.id || "",
        name: group.name || "",
        workspaceName: group.workspace?.name,
      } as ShareGroup,
      shareStatus: groupPermission,
      canModifyPermissions: canModify,
      availablePermissions,
    };
  });

  const programItems: ShareItemData[] = programs.map((program) => ({
    type: "program" as const,
    item: {
      id: program.id || "",
      name: program.name || "",
      subtitle: "Program Participants",
    },
    shareStatus: "VIEW" as SharePermissionState,
    canModifyPermissions: false,
    availablePermissions: [],
  }));

  return [...userItems, ...groupItems, ...programItems];
}

/**
 * Check if any permissions have been modified
 */
export function hasPermissionChanges(
  users: ResourcePermissionUser[],
  groups: ResourcePermissionGroup[],
): boolean {
  const userChanges = users.some(
    (user) =>
      user.shareStatus === "REMOVE" ||
      user.shareStatus !== user.originalPermissionLevel,
  );

  const groupChanges = groups.some(
    (group) =>
      group.shareStatus === "REMOVE" ||
      group.shareStatus !== group.originalPermissionLevel,
  );

  return userChanges || groupChanges;
}

/**
 * Filter permissions that need to be updated
 */
export function getPermissionsToUpdate(
  users: ResourcePermissionUser[],
  groups: ResourcePermissionGroup[],
): {
  userPermissions: Array<{
    id: string;
    permissionLevel?: PermissionLevel;
    remove?: boolean;
  }>;
  groupPermissions: Array<{
    id: string;
    permissionLevel?: PermissionLevel;
    remove?: boolean;
  }>;
} {
  const userPermissions = users
    .filter(
      (user) =>
        user.id &&
        (user.shareStatus === "REMOVE" ||
          user.shareStatus !== user.originalPermissionLevel),
    )
    .map((user) => ({
      id: user.id!,
      permissionLevel:
        user.shareStatus === "REMOVE"
          ? undefined
          : (user.shareStatus as PermissionLevel),
      remove: user.shareStatus === "REMOVE" ? true : undefined,
    }));

  const groupPermissions = groups
    .filter(
      (group) =>
        group.id &&
        (group.shareStatus === "REMOVE" ||
          group.shareStatus !== group.originalPermissionLevel),
    )
    .map((group) => ({
      id: group.id!,
      permissionLevel:
        group.shareStatus === "REMOVE"
          ? undefined
          : (group.shareStatus as PermissionLevel),
      remove: group.shareStatus === "REMOVE" ? true : undefined,
    }));

  return {
    userPermissions,
    groupPermissions,
  };
}
